<template>
	<section
		class="analytics-panel flex flex-col rounded-xl bg-surface-2 ring-1 ring-inset ring-white/[0.05] shadow-[0_2px_6px_rgba(0,0,0,0.28)]"
		:class="[padded ? 'p-4 md:p-5' : '']"
	>
		<header v-if="title || $slots.actions || $slots.header" class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" :class="padded ? '' : 'px-4 pt-4 md:px-5 md:pt-5'">
			<div class="min-w-0">
				<slot name="header">
					<h2 class="mb-0 text-base font-semibold leading-6 text-surface-text-2">{{ title }}</h2>
					<p v-if="subtitle" class="mb-0 mt-0.5 text-xs leading-4 text-surface-7">{{ subtitle }}</p>
				</slot>
			</div>
			<div v-if="$slots.actions" class="flex min-w-0 max-w-full items-center gap-2">
				<slot name="actions" />
			</div>
		</header>

		<!-- Full-width controls row (tabs, filters) that stays visible in every state. -->
		<div v-if="$slots.toolbar" class="mt-3 min-w-0" :class="padded ? '' : 'px-4 md:px-5'">
			<slot name="toolbar" />
		</div>

		<div class="relative flex-1 min-w-0" :class="[title || $slots.actions || $slots.header || $slots.toolbar ? 'mt-4' : '', bodyClass]">
			<!-- Loading -->
			<div v-if="state === 'loading'" class="flex flex-col gap-3" :style="{ minHeight }" aria-busy="true">
				<slot name="skeleton">
					<div class="h-4 w-1/3 animate-pulse rounded bg-surface-3" />
					<div class="h-4 w-2/3 animate-pulse rounded bg-surface-3/80" />
					<div class="h-4 w-1/2 animate-pulse rounded bg-surface-3/60" />
				</slot>
			</div>

			<!-- Error / ambiguous [] from core -->
			<div v-else-if="state === 'error'" class="flex flex-col items-center justify-center text-center px-4" :style="{ minHeight }">
				<p class="mb-1 text-sm font-medium text-surface-text-2">Data unavailable</p>
				<p class="mb-3 max-w-xs text-xs leading-5 text-surface-7">{{ errorText }}</p>
				<FormButton type="secondary" size="sm" @click="$emit('retry')">Retry</FormButton>
			</div>

			<!-- Empty -->
			<div v-else-if="state === 'empty'" class="flex flex-col items-center justify-center text-center px-4" :style="{ minHeight }">
				<p class="mb-1 text-sm font-medium text-surface-text-2">{{ emptyTitle }}</p>
				<p class="mb-0 max-w-xs text-xs leading-5 text-surface-7">{{ emptyText }}</p>
				<div class="mt-3 flex items-center gap-2">
					<slot name="emptyActions" />
					<FormButton v-if="emptyRetry" type="secondary" size="sm" @click="$emit('retry')">Retry</FormButton>
				</div>
			</div>

			<slot v-else />
		</div>
	</section>
</template>

<script setup>
import FormButton from '@/components/Atoms/FormButton.vue';

defineProps({
	title: { type: String, default: '' },
	subtitle: { type: String, default: '' },
	// 'ready' | 'loading' | 'empty' | 'error'
	state: { type: String, default: 'ready' },
	padded: { type: Boolean, default: true },
	bodyClass: { type: String, default: '' },
	minHeight: { type: String, default: '200px' },
	emptyTitle: { type: String, default: 'No analytics for this period' },
	emptyText: {
		type: String,
		default: 'No player activity was recorded for the selected content and dates. Try a different date range or selection.',
	},
	errorText: { type: String, default: 'We could not load this metric.' },
	// Core returns [] for both "no data" and upstream errors, so the empty
	// state offers a retry as well.
	emptyRetry: { type: Boolean, default: true },
});

defineEmits(['retry']);
</script>
